// src/lib/billingMath.ts

export interface InvoiceSummary {
  isSale: boolean;
  isGst: boolean;
  orderId: string;
  discountAmount: number;
  invoiceGrossTotal: number;
  netPayable: number;
  uiTotalGst: number;
  discountedItems: any[];
  hsnSummary: Record<string, { taxable: number; cgst: number; sgst: number }>;
  firstTransaction: any;
}

/**
 * Universally calculates the breakdown of an invoice group.
 * Handles both live cart previews (Dashboard) and historical records (Transactions).
 */
export function calculateInvoiceSummary(group: any[], products: any[]): InvoiceSummary | null {
  if (!group || group.length === 0) return null;

  const first = group[0];
  const isSale = first.type === "sale";

  // 1. Order ID Safety Check for unsaved Dashboard items
  let orderId = "DRAFT";
  if (first.fullOrderId) {
    orderId = String(first.fullOrderId);
  } else if (first.id) {
    orderId = String(first.id).split("_")[0];
  }

  const isGst = orderId.startsWith("INV-") || !!first.isGstBill;

  // 2. Identify and filter items
  const filteredSaleItems = group.filter((t: any) => !((t.productName || '').toLowerCase().includes("discount")));
  const discountItem = group.find((t: any) => (t.productName || '').toLowerCase().includes("discount"));

  // 3. Calculate Gross Total (Needed for percentage math)
  const invoiceGrossTotal = filteredSaleItems.reduce((sum: number, item: any) =>
    sum + Number(item.quantity * item.rate || 0), 0
  );

  // 4. Internalized Discount Logic [The "Brain" for Cash/Percentage logic]
  let discountAmount = 0;
  if (discountItem) {
    const rawVal = Math.abs(Number(discountItem.total) || (discountItem.quantity * discountItem.rate));

    if (discountItem.isPercentage) {
      // Calculate percentage and round to 2 decimals immediately for precision
      discountAmount = Math.round(((invoiceGrossTotal * rawVal) / 100 + Number.EPSILON) * 100) / 100;
    } else {
      discountAmount = Math.round((rawVal + Number.EPSILON) * 100) / 100;
    }
  }

  // 5. Pre-calculate per-item discount shares using largest remainder method.
  // Guarantees: sum(roundedDiscountShares) === discountAmount exactly — no drift possible.
  const rawItemTotals = filteredSaleItems.map((item: any) => Number(item.quantity * item.rate || 0));
  const roundedDiscountShares: number[] = rawItemTotals.map(rawTotal =>
    invoiceGrossTotal > 0 ? Math.round(((rawTotal / invoiceGrossTotal) * discountAmount + Number.EPSILON) * 100) / 100 : 0
  );
  const sumRoundedShares = roundedDiscountShares.reduce((a, b) => a + b, 0);
  const discountRemainder = Math.round((discountAmount - sumRoundedShares) * 100) / 100;
  if (discountRemainder !== 0 && rawItemTotals.length > 0) {
    const largestIdx = rawItemTotals.indexOf(Math.max(...rawItemTotals));
    roundedDiscountShares[largestIdx] = Math.round((roundedDiscountShares[largestIdx] + discountRemainder + Number.EPSILON) * 100) / 100;
  }

  let uiTotalGst = 0;
  const hsnSummary: Record<string, { taxable: number; cgst: number; sgst: number }> = {};

  const discountedItems = filteredSaleItems.map((item: any, idx: number) => {
    // Free scheme items contribute ₹0 to every total — pass through untouched
    if (item.isFreeItem) {
      return {
        ...item,
        rate: 0, total: 0, visualRate: 0, visualTotal: 0,
        taxableAmount: 0, cgst: 0, sgst: 0, hsn: "N/A",
        _cartKey: item._cartKey || item.productId,
      };
    }

    const product = products.find((p: any) => p.id === item.productId) || {};
    const hsn = item.hsn || product?.hsn || "N/A";
    const gstRate = isGst ? (item.gstRate ?? product?.gstRate ?? 0) : 0;
    const isInclusive = item.gstInclusive ?? product?.gstInclusive ?? false;

    const rawItemTotal = rawItemTotals[idx];

    const discountedTotalBase = Math.round((rawItemTotal - roundedDiscountShares[idx] + Number.EPSILON) * 100) / 100;

    // 6. Compute rounded components without accumulation drift.
    // Inclusive: pin displayTotal = discountedTotalBase, derive rTaxable from remainder.
    // Exclusive: round totalTax first, then split so rCgst + rSgst = rTotalTax exactly.
    let rCgst = 0, rSgst = 0, rTaxable: number, displayTotal: number;

    if (item.taxableAmount !== undefined) {
      // A. Stored Firestore values — round and sum as-is
      rTaxable = Math.round((Number(item.taxableAmount) + Number.EPSILON) * 100) / 100;
      rCgst = Math.round(((Number(item.cgst) || 0) + Number.EPSILON) * 100) / 100;
      rSgst = Math.round(((Number(item.sgst) || 0) + Number.EPSILON) * 100) / 100;
      displayTotal = rTaxable + rCgst + rSgst;
    } else if (gstRate > 0 && isInclusive) {
      // B. GST-inclusive: extract tax, then derive taxable so rTaxable + rCgst + rSgst = discountedTotalBase exactly
      const totalTax = discountedTotalBase - discountedTotalBase / (1 + gstRate / 100);
      rCgst = Math.round((totalTax / 2 + Number.EPSILON) * 100) / 100;
      rSgst = Math.round((totalTax / 2 + Number.EPSILON) * 100) / 100;
      rTaxable = discountedTotalBase - rCgst - rSgst; // exact 2-decimal arithmetic — no rounding needed
      displayTotal = discountedTotalBase; // always exact — GST drift eliminated
    } else if (gstRate > 0) {
      // C. GST-exclusive: round total tax first, then split to absorb half-cent remainder into sgst
      const rTotalTax = Math.round((discountedTotalBase * (gstRate / 100) + Number.EPSILON) * 100) / 100;
      rCgst = Math.round((rTotalTax / 2 + Number.EPSILON) * 100) / 100;
      rSgst = Math.round((rTotalTax - rCgst + Number.EPSILON) * 100) / 100; // guarantees rCgst + rSgst = rTotalTax
      rTaxable = discountedTotalBase;
      displayTotal = rTaxable + rCgst + rSgst;
    } else {
      // D. No GST
      rTaxable = discountedTotalBase;
      displayTotal = discountedTotalBase;
    }

    const displayRate = displayTotal / (Number(item.quantity) || 1);

    // Update aggregates using rounded values
    uiTotalGst += (rCgst + rSgst);
    if (!hsnSummary[hsn]) hsnSummary[hsn] = { taxable: 0, cgst: 0, sgst: 0 };
    hsnSummary[hsn].taxable += rTaxable;
    hsnSummary[hsn].cgst += rCgst;
    hsnSummary[hsn].sgst += rSgst;

    return {
      ...item,
      rate: displayRate,
      total: displayTotal,
      visualRate: displayRate,
      visualTotal: displayTotal,
      taxableAmount: rTaxable,
      cgst: rCgst,
      sgst: rSgst,
      hsn,
      _cartKey: item._cartKey || item.productId,
    };
  });

  // 7. Net Payable is always the sum of individually rounded line totals
  const rawNetPayable = isSale
    ? discountedItems.reduce((sum: number, t: any) => sum + t.total, 0)
    : group.reduce((sum: number, t: any) => sum + (Number(t.total) || 0), 0);
  const netPayable = Math.round((rawNetPayable + Number.EPSILON) * 100) / 100;

  return {
    isSale,
    isGst,
    orderId,
    discountAmount,
    invoiceGrossTotal,
    netPayable,
    uiTotalGst,
    discountedItems,
    hsnSummary,
    firstTransaction: first
  };
}